import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import { Layers, Map, MapPin, Eye, X, RefreshCw, Lightbulb, Home } from 'lucide-react';
import { useObras } from '../../obras/context/ObrasContextSupabase';
import { useObrasFixas } from '../../obras-fixas/context/ObrasFixasContextSupabase';
import PostesIluminacaoProvider, { usePostesIluminacao } from '../../postes-iluminacao/context/PostesIluminacaoProvider';
import FossasSepticasProvider, { useFossasSepticas } from '../../fossas-septicas/context/FossasSepticasProvider';
import { createPosteIcon } from '../../postes-iluminacao/utils/mapHelpers';
import { createCustomIcon } from '../../fossas-septicas/utils/mapHelpers';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Função para criar ícones personalizados baseados no status
const criarIconePersonalizado = (status) => {
  const cor = getCorPorStatus(status);
  
  return L.divIcon({
    className: 'obra-fixa-marker',
    html: `<div style="background-color: ${cor}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });
};

// Função auxiliar para determinar a cor baseada no status
function getCorPorStatus(status) {
  switch (status) {
    case 'Planejada':
      return '#ffc107'; // Amarelo
    case 'Em execução':
      return '#28a745'; // Verde
    case 'Finalizada':
      return '#007bff'; // Azul
    default:
      return '#6c757d'; // Cinza
  }
}

// Componente interno que usa os hooks dos contextos
const MapaIntegradoContent = () => {
  const { obras } = useObras();
  const { obrasFixas } = useObrasFixas();
  const { postes } = usePostesIluminacao();
  const { 
    propriedades, 
    residencias, 
    fossas, 
    carregarPropriedades, 
    carregarResidencias, 
    carregarFossas 
  } = useFossasSepticas();
  
  const [tipoMapa, setTipoMapa] = useState('obras'); // 'obras', 'postes', 'fossas'
  const [mostrarObras, setMostrarObras] = useState(true);
  const [mostrarObrasFixas, setMostrarObrasFixas] = useState(true);
  const [mostrarPropriedades, setMostrarPropriedades] = useState(true);
  const [mostrarResidencias, setMostrarResidencias] = useState(true);
  const [mostrarFossas, setMostrarFossas] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState({
    'Planejada': true,
    'Em execução': true,
    'Finalizada': true
  });
  const [filtroStatusPostes, setFiltroStatusPostes] = useState({
    'Funcionando': true,
    'Alerta': true,
    'Defeito': true
  });
  const [filtroStatusFossas, setFiltroStatusFossas] = useState({
    'Mapeada': true,
    'Não Mapeada': true,
    'Em Análise': true
  });
  const [obraSelecionada, setObraSelecionada] = useState(null);
  const [obraFixaSelecionada, setObraFixaSelecionada] = useState(null);
  const [dadosAtualizados, setDadosAtualizados] = useState({
    obras: [],
    obrasFixas: [],
    postes: [],
    propriedades: [],
    residencias: [],
    fossas: []
  });
  const intervalRef = useRef(null);
  const ultimaAtualizacao = useRef(Date.now());
  
  // Tipos de mapa disponíveis
  const tiposMapaDisponiveis = [
    {
      id: 'obras',
      nome: 'Obras',
      descricao: 'Obras em vias públicas e obras fixas',
      icon: <Map size={20} />,
      color: '#667eea'
    },
    {
      id: 'postes',
      nome: 'Iluminação',
      descricao: 'Postes de iluminação pública',
      icon: <Lightbulb size={20} />,
      color: '#ffc107'
    },
    {
      id: 'fossas',
      nome: 'Fossas Sépticas',
      descricao: 'Propriedades rurais e fossas sépticas',
      icon: <Home size={20} />,
      color: '#28a745'
    }
  ];
  
  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  
  // Função para buscar dados atualizados do servidor
  const buscarDadosAtualizados = async () => {
    try {
      // Verificar se passou pelo menos 30 segundos desde a última atualização
      const agora = Date.now();
      if (agora - ultimaAtualizacao.current < 30000) {
        return; // Não atualizar se passou menos de 30 segundos
      }
      
      console.log("Buscando dados atualizados...");
      
      // Importar o cliente Supabase
      const supabase = (await import('../../../config/supabaseClient')).default;
      
      // Buscar obras atualizadas
      const { data: obrasData, error: obrasError } = await supabase
        .from('obras')
        .select('*');
      
      if (obrasError) {
        console.error("Erro ao buscar obras:", obrasError);
      } else if (obrasData) {
        // Para cada obra, buscar seus trechos e itens
        const obrasCompletas = await Promise.all(obrasData.map(async (obra) => {
          // Buscar trechos da obra
          const { data: trechosData } = await supabase
            .from('trechos')
            .select('*')
            .eq('obra_id', obra.id);
          
          // Buscar derivações para cada trecho
          const trechosComDerivacoes = await Promise.all((trechosData || []).map(async (trecho) => {
            const { data: derivacoesData } = await supabase
              .from('derivacoes')
              .select('*')
              .eq('trecho_id', trecho.id);
            
            return {
              id: trecho.id,
              nome: trecho.nome,
              tipo: trecho.tipo,
              obra_id: trecho.obra_id,
              distanciaMetros: trecho.distancia_metros,
              pontos: trecho.pontos ? JSON.parse(trecho.pontos) : [],
              derivacoes: (derivacoesData || []).map(d => ({
                id: d.id,
                nome: d.nome,
                pontoOrigem: d.ponto_origem,
                trecho_id: d.trecho_id,
                distanciaMetros: d.distancia_metros,
                pontos: d.pontos ? JSON.parse(d.pontos) : []
              }))
            };
          }));
          
          // Buscar itens da obra
          const { data: itensData } = await supabase
            .from('itens')
            .select('*')
            .eq('obra_id', obra.id);
          
          // Retornar obra completa com trechos e itens, convertendo snake_case para camelCase
          return {
            id: obra.id,
            nome: obra.nome,
            empreiteiro: obra.empreiteiro,
            valor: obra.valor,
            status: obra.status,
            dataInicio: obra.data_inicio,
            dataPrevisaoTermino: obra.data_previsao_termino,
            observacoes: obra.observacoes,
            contrato: obra.contrato,
            licitacao: obra.licitacao,
            processo: obra.processo,
            gestor: obra.gestor,
            fiscaisObras: obra.fiscais_obras,
            totalMedicoes: obra.total_medicoes,
            trechos: trechosComDerivacoes || [],
            itens: itensData || []
          };
        }));
        
        // Atualizar o estado com os dados mais recentes
        setDadosAtualizados(prev => ({
          ...prev,
          obras: obrasCompletas
        }));
      }
      
      // Buscar obras fixas atualizadas
      const { data: obrasFixasData, error: obrasFixasError } = await supabase
        .from('obras_fixas')
        .select('*');
      
      if (obrasFixasError) {
        console.error("Erro ao buscar obras fixas:", obrasFixasError);
      } else if (obrasFixasData) {
        // Para cada obra fixa, buscar seus itens
        const obrasFixasCompletas = await Promise.all(obrasFixasData.map(async (obraFixa) => {
          // Buscar itens da obra fixa
          const { data: itensData } = await supabase
            .from('itens')
            .select('*')
            .eq('obra_fixa_id', obraFixa.id);
          
          // Retornar obra fixa completa com itens, convertendo snake_case para camelCase
          return {
            id: obraFixa.id,
            nome: obraFixa.nome,
            empreiteiro: obraFixa.empreiteiro,
            valor: obraFixa.valor,
            status: obraFixa.status,
            dataInicio: obraFixa.data_inicio,
            dataPrevisaoTermino: obraFixa.data_previsao_termino,
            observacoes: obraFixa.observacoes,
            contrato: obraFixa.contrato,
            licitacao: obraFixa.licitacao,
            processo: obraFixa.processo,
            gestor: obraFixa.gestor,
            fiscaisObras: obraFixa.fiscais_obras,
            totalMedicoes: obraFixa.total_medicoes,
            // Campos específicos para obras fixas
            localizacao: {
              latitude: obraFixa.latitude,
              longitude: obraFixa.longitude,
              nome: obraFixa.nome_local,
              descricao: obraFixa.descricao_local
            },
            itens: itensData || []
          };
        }));
        
        // Atualizar o estado com os dados mais recentes
        setDadosAtualizados(prev => ({
          ...prev,
          obrasFixas: obrasFixasCompletas
        }));
      }
      
      ultimaAtualizacao.current = agora;
      console.log("Dados atualizados com sucesso!");
    } catch (error) {
      console.error("Erro ao buscar dados atualizados:", error);
    }
  };
  
  // Inicializar os dados com os valores dos contextos
  useEffect(() => {
    setDadosAtualizados({
      obras: obras || [],
      obrasFixas: obrasFixas || [],
      postes: postes || [],
      propriedades: propriedades || [],
      residencias: residencias || [],
      fossas: fossas || []
    });
  }, [obras, obrasFixas, postes, propriedades, residencias, fossas]);
  
  // Carregar dados das fossas sépticas quando o tipo de mapa for fossas
  useEffect(() => {
    if (tipoMapa === 'fossas') {
      // Forçar carregamento dos dados
      if (carregarPropriedades) carregarPropriedades();
      if (carregarResidencias) carregarResidencias();
      if (carregarFossas) carregarFossas();
    }
  }, [tipoMapa, carregarPropriedades, carregarResidencias, carregarFossas]);
  
  // Configurar intervalo para atualização periódica
  useEffect(() => {
    // Atualizar a cada 1 minuto
    intervalRef.current = setInterval(() => {
      buscarDadosAtualizados();
    }, 60000); // 60 segundos
    
    // Limpar intervalo ao desmontar o componente
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  // Adicionar botão de atualização manual no mapa
  const AtualizarButton = () => {
    const [isLoading, setIsLoading] = useState(false);
    
    const handleClick = async () => {
      setIsLoading(true);
      await buscarDadosAtualizados();
      setTimeout(() => setIsLoading(false), 1000); // Mostrar o spinner por pelo menos 1 segundo
    };
    
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: 'none',
          cursor: isLoading ? 'default' : 'pointer'
        }}
        title="Atualizar dados do mapa"
      >
        <RefreshCw 
          size={20} 
          color="#333" 
          className={isLoading ? 'rotating' : ''} 
          style={{
            animation: isLoading ? 'spin 1s linear infinite' : 'none'
          }}
        />
      </button>
    );
  };

  // Filtrar dados por status
  const obrasFiltradasPorStatus = dadosAtualizados.obras.filter(obra => filtroStatus[obra.status]);
  const obrasFixasFiltradasPorStatus = dadosAtualizados.obrasFixas.filter(obraFixa => filtroStatus[obraFixa.status]);
  const postesFiltradasPorStatus = dadosAtualizados.postes.filter(poste => filtroStatusPostes[poste.status]);
  
  // Filtrar propriedades por status
  const propriedadesFiltradasPorStatus = dadosAtualizados.propriedades.filter(prop => filtroStatusFossas[prop.status] || !prop.status);
  
  // Obter IDs das propriedades filtradas para filtrar residências e fossas filhas
  const propriedadesIds = propriedadesFiltradasPorStatus.map(prop => prop.id);
  
  // Filtrar residências apenas das propriedades que passaram no filtro de status
  const residenciasFiltradasPorStatus = dadosAtualizados.residencias.filter(res => 
    res.latitude && 
    res.longitude && 
    res.propriedade_id && 
    propriedadesIds.includes(res.propriedade_id)
  );
  
  // Filtrar fossas apenas das residências que pertencem às propriedades filtradas
  const residenciasIds = residenciasFiltradasPorStatus.map(res => res.id);
  const fossasFiltradasPorStatus = dadosAtualizados.fossas.filter(fossa => 
    fossa.latitude && 
    fossa.longitude && 
    fossa.residencia_id && 
    residenciasIds.includes(fossa.residencia_id)
  );

  // Formatar valor monetário
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Formatar data
  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };
  
  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };
  
  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;
    
    const totalValor = itens.reduce((total, item) => total + (item.total || 0), 0);
    if (totalValor === 0) return 0;
    
    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .rotating {
            animation: spin 1s linear infinite;
          }
        `}
      </style>

      <MapContainer
        center={centroConceicaoCastelo}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Renderizar conteúdo baseado no tipo de mapa selecionado */}
        {tipoMapa === 'obras' && (
          <>
            {/* Renderizar obras lineares (trechos) */}
            {mostrarObras && obrasFiltradasPorStatus.map(obra => (
              <React.Fragment key={obra.id}>
                {obra.trechos && obra.trechos.map((trecho, index) => (
                  <React.Fragment key={`${obra.id}-${index}`}>
                    {/* Trecho principal */}
                    <Polyline
                      positions={trecho.pontos}
                      pathOptions={{ 
                        color: getCorPorStatus(obra.status),
                        weight: 5,
                        opacity: 0.7
                      }}
                      eventHandlers={{
                        click: () => setObraSelecionada(obra)
                      }}
                    />
                    
                    {/* Derivações do trecho */}
                    {trecho.derivacoes && trecho.derivacoes.map((derivacao, derivIndex) => (
                      <Polyline
                        key={`${obra.id}-${index}-${derivIndex}`}
                        positions={derivacao.pontos}
                        pathOptions={{ 
                          color: getCorPorStatus(obra.status),
                          weight: 3,
                          opacity: 0.7,
                          dashArray: "5, 5"
                        }}
                        eventHandlers={{
                          click: () => setObraSelecionada(obra)
                        }}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            
            {/* Renderizar obras fixas (pontos) */}
            {mostrarObrasFixas && obrasFixasFiltradasPorStatus.map(obraFixa => (
              obraFixa.localizacao && obraFixa.localizacao.latitude && obraFixa.localizacao.longitude && (
                <Marker 
                  key={obraFixa.id} 
                  position={[obraFixa.localizacao.latitude, obraFixa.localizacao.longitude]}
                  icon={criarIconePersonalizado(obraFixa.status)}
                  eventHandlers={{
                    click: () => setObraFixaSelecionada(obraFixa)
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: '200px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{obraFixa.nome}</h4>
                      {obraFixa.localizacao.nome && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Local:</strong> {obraFixa.localizacao.nome}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Empreiteiro:</strong> {obraFixa.empreiteiro}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Status:</strong> <span style={{ color: getCorPorStatus(obraFixa.status) }}>{obraFixa.status}</span>
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Valor:</strong> {formatarMoeda(obraFixa.valor)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </>
        )}
        
        {/* Renderizar postes de iluminação */}
        {tipoMapa === 'postes' && postesFiltradasPorStatus.map(poste => (
          poste.latitude && poste.longitude && !isNaN(parseFloat(poste.latitude)) && !isNaN(parseFloat(poste.longitude)) && (
            <Marker 
              key={poste.id} 
              position={[parseFloat(poste.latitude), parseFloat(poste.longitude)]}
              icon={createPosteIcon(poste.status)}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Poste #{poste.numero_identificacao}</h4>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>Logradouro:</strong> {poste.logradouro}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>Tipo de Lâmpada:</strong> {poste.tipo_lampada}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>Potência:</strong> {poste.potencia}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    <strong>Status:</strong> {poste.status}
                  </p>
                  {poste.observacoes && (
                    <p style={{ margin: '5px 0', fontSize: '14px' }}>
                      <strong>Observações:</strong> {poste.observacoes}
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          )
        ))}
        
        {/* Renderizar dados de fossas sépticas */}
        {tipoMapa === 'fossas' && (
          <>
            {/* Propriedades rurais */}
            {mostrarPropriedades && propriedadesFiltradasPorStatus.map(propriedade => (
              propriedade.latitude && propriedade.longitude && !isNaN(parseFloat(propriedade.latitude)) && !isNaN(parseFloat(propriedade.longitude)) && (
                <Marker 
                  key={`prop-${propriedade.id}`} 
                  position={[parseFloat(propriedade.latitude), parseFloat(propriedade.longitude)]}
                  icon={createCustomIcon(propriedade.status || 'Mapeada', 'propriedade')}
                >
                  <Popup>
                    <div style={{ minWidth: '250px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{propriedade.nome_propriedade || 'Propriedade Rural'}</h4>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Proprietário:</strong> {propriedade.nome_proprietario || 'Não informado'}
                      </p>
                      {propriedade.cpf_cnpj && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>CPF/CNPJ:</strong> {propriedade.cpf_cnpj}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Localização:</strong> {propriedade.localizacao || 'Não informada'}
                      </p>
                      {propriedade.area_hectares && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Área:</strong> {propriedade.area_hectares} hectares
                        </p>
                      )}
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Status:</strong> {propriedade.status || 'Não definido'}
                      </p>
                      {propriedade.localidade && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Localidade:</strong> {propriedade.localidade.nome}
                        </p>
                      )}
                      {propriedade.agente && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Agente:</strong> {propriedade.agente.nome}
                        </p>
                      )}
                      {propriedade.telefone && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Telefone:</strong> {propriedade.telefone}
                        </p>
                      )}
                      {propriedade.residencias && propriedade.residencias.length > 0 && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Residências:</strong> {propriedade.residencias.length}
                        </p>
                      )}
                      {propriedade.fossas && propriedade.fossas.length > 0 && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Fossas:</strong> {propriedade.fossas.length}
                        </p>
                      )}
                      {propriedade.observacoes && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Observações:</strong> {propriedade.observacoes}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
            
            {/* Residências */}
            {mostrarResidencias && residenciasFiltradasPorStatus.map(residencia => {
              return residencia.latitude && residencia.longitude && !isNaN(parseFloat(residencia.latitude)) && !isNaN(parseFloat(residencia.longitude)) ? (
                <Marker 
                  key={`res-${residencia.id}`} 
                  position={[parseFloat(residencia.latitude), parseFloat(residencia.longitude)]}
                  icon={createCustomIcon(residencia.status || 'Mapeada', 'residencia')}
                >
                  <Popup>
                    <div style={{ minWidth: '250px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Residência #{residencia.identificacao || residencia.id}</h4>
                      {residencia.endereco && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Endereço:</strong> {residencia.endereco}
                        </p>
                      )}
                      {residencia.nome_morador && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Morador:</strong> {residencia.nome_morador}
                        </p>
                      )}
                      {residencia.cpf_morador && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>CPF:</strong> {residencia.cpf_morador}
                        </p>
                      )}
                      {residencia.telefone && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Telefone:</strong> {residencia.telefone}
                        </p>
                      )}
                      {residencia.numero_moradores && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Moradores:</strong> {residencia.numero_moradores} pessoas
                        </p>
                      )}
                      {residencia.propriedade && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Propriedade:</strong> {residencia.propriedade.nome}
                        </p>
                      )}
                      {residencia.observacoes && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Observações:</strong> {residencia.observacoes}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ) : null;
            })}
            
            {/* Fossas sépticas */}
            {mostrarFossas && fossasFiltradasPorStatus.map(fossa => {
              return fossa.latitude && fossa.longitude && !isNaN(parseFloat(fossa.latitude)) && !isNaN(parseFloat(fossa.longitude)) ? (
                <Marker 
                  key={`fossa-${fossa.id}`} 
                  position={[parseFloat(fossa.latitude), parseFloat(fossa.longitude)]}
                  icon={createCustomIcon(fossa.estado_conservacao || 'Mapeada', 'fossa')}
                >
                  <Popup>
                    <div style={{ minWidth: '250px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Fossa Séptica</h4>
                      {fossa.tipo && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Tipo:</strong> {fossa.tipo}
                        </p>
                      )}
                      {fossa.capacidade && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Capacidade:</strong> {fossa.capacidade} pessoas
                        </p>
                      )}
                      {fossa.material && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Material:</strong> {fossa.material}
                        </p>
                      )}
                      {fossa.estado_conservacao && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Estado:</strong> {fossa.estado_conservacao}
                        </p>
                      )}
                      {fossa.residencia && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Residência:</strong> #{fossa.residencia.identificacao || fossa.residencia.id}
                        </p>
                      )}
                      {fossa.data_instalacao && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Instalação:</strong> {formatarData(fossa.data_instalacao)}
                        </p>
                      )}
                      {fossa.data_ultima_limpeza && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Última Limpeza:</strong> {formatarData(fossa.data_ultima_limpeza)}
                        </p>
                      )}
                      {fossa.observacoes && (
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Observações:</strong> {fossa.observacoes}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ) : null;
            })}
          </>
        )}
      </MapContainer>
      
      {/* Botão para mostrar/ocultar filtros */}
      <button
        onClick={() => setMostrarFiltros(!mostrarFiltros)}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: 'none',
          cursor: 'pointer'
        }}
      >
        <Layers size={20} color="#333" />
      </button>
      
      {/* Painel de filtros */}
      {mostrarFiltros && (
        <div style={{
          position: 'absolute',
          top: '60px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '15px',
          width: '280px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Mapa Integrado</h3>
            <button
              onClick={() => setMostrarFiltros(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              <X size={16} color="#666" />
            </button>
          </div>
          
          {/* Seletor de tipo de mapa */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Tipo de Mapa</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tiposMapaDisponiveis.map(tipo => (
                <label 
                  key={tipo.id}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    backgroundColor: tipoMapa === tipo.id ? '#f0f8ff' : 'transparent',
                    border: tipoMapa === tipo.id ? '1px solid #0066cc' : '1px solid transparent'
                  }}
                >
                  <input
                    type="radio"
                    name="tipoMapa"
                    value={tipo.id}
                    checked={tipoMapa === tipo.id}
                    onChange={() => setTipoMapa(tipo.id)}
                    style={{ margin: 0 }}
                  />
                  <div style={{ color: tipo.color }}>
                    {tipo.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px' }}>{tipo.nome}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>{tipo.descricao}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          {/* Filtros específicos para obras */}
          {tipoMapa === 'obras' && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Tipos de Obras</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={mostrarObras}
                    onChange={() => setMostrarObras(!mostrarObras)}
                  />
                  <Map size={16} style={{ color: '#667eea' }} />
                  <span>Obras Lineares</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={mostrarObrasFixas}
                    onChange={() => setMostrarObrasFixas(!mostrarObrasFixas)}
                  />
                  <MapPin size={16} style={{ color: '#9c27b0' }} />
                  <span>Obras Fixas</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Filtros de status para obras */}
          {tipoMapa === 'obras' && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Status</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatus['Planejada']}
                    onChange={() => setFiltroStatus({...filtroStatus, 'Planejada': !filtroStatus['Planejada']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#ffc107', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Planejada</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatus['Em execução']}
                    onChange={() => setFiltroStatus({...filtroStatus, 'Em execução': !filtroStatus['Em execução']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#28a745', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Em execução</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatus['Finalizada']}
                    onChange={() => setFiltroStatus({...filtroStatus, 'Finalizada': !filtroStatus['Finalizada']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#007bff', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Finalizada</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Filtros para postes de iluminação */}
          {tipoMapa === 'postes' && (
            <div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Status dos Postes</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatusPostes['Funcionando']}
                    onChange={() => setFiltroStatusPostes({...filtroStatusPostes, 'Funcionando': !filtroStatusPostes['Funcionando']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#4caf50', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Funcionando</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatusPostes['Alerta']}
                    onChange={() => setFiltroStatusPostes({...filtroStatusPostes, 'Alerta': !filtroStatusPostes['Alerta']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#ff9800', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Alerta</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filtroStatusPostes['Defeito']}
                    onChange={() => setFiltroStatusPostes({...filtroStatusPostes, 'Defeito': !filtroStatusPostes['Defeito']})}
                  />
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: '#f44336', 
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}></span>
                  <span>Defeito</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Filtros para fossas sépticas */}
          {tipoMapa === 'fossas' && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Tipos de Dados</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={mostrarPropriedades}
                      onChange={() => setMostrarPropriedades(!mostrarPropriedades)}
                    />
                    <Home size={16} style={{ color: '#28a745' }} />
                    <span>Propriedades</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={mostrarResidencias}
                      onChange={() => setMostrarResidencias(!mostrarResidencias)}
                    />
                    <MapPin size={16} style={{ color: '#007bff' }} />
                    <span>Residências</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={mostrarFossas}
                      onChange={() => setMostrarFossas(!mostrarFossas)}
                    />
                    <span style={{ fontSize: '16px' }}>🚽</span>
                    <span>Fossas Sépticas</span>
                  </label>
                </div>
              </div>
              
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Status</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={filtroStatusFossas['Mapeada']}
                      onChange={() => setFiltroStatusFossas({...filtroStatusFossas, 'Mapeada': !filtroStatusFossas['Mapeada']})}
                    />
                    <span style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: '#28a745', 
                      borderRadius: '50%',
                      display: 'inline-block'
                    }}></span>
                    <span>Mapeada</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={filtroStatusFossas['Não Mapeada']}
                      onChange={() => setFiltroStatusFossas({...filtroStatusFossas, 'Não Mapeada': !filtroStatusFossas['Não Mapeada']})}
                    />
                    <span style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: '#dc3545', 
                      borderRadius: '50%',
                      display: 'inline-block'
                    }}></span>
                    <span>Não Mapeada</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={filtroStatusFossas['Em Análise']}
                      onChange={() => setFiltroStatusFossas({...filtroStatusFossas, 'Em Análise': !filtroStatusFossas['Em Análise']})}
                    />
                    <span style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: '#ffc107', 
                      borderRadius: '50%',
                      display: 'inline-block'
                    }}></span>
                    <span>Em Análise</span>
                  </label>
                </div>
              </div>
            </>
          )}
          
          {/* Estatísticas condicionais */}
          {tipoMapa === 'obras' && (
            <>
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
                <p style={{ margin: '0' }}>
                  Total: {mostrarObras ? obrasFiltradasPorStatus.length : 0} obras lineares, {' '}
                  {mostrarObrasFixas ? obrasFixasFiltradasPorStatus.length : 0} obras fixas
                </p>
              </div>
              
              <div style={{ marginTop: '10px', fontSize: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '10px', height: '10px', backgroundColor: '#ffc107', borderRadius: '50%', display: 'inline-block' }}></span>
                    <span>Planejada</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '10px', height: '10px', backgroundColor: '#28a745', borderRadius: '50%', display: 'inline-block' }}></span>
                    <span>Em execução</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '10px', height: '10px', backgroundColor: '#007bff', borderRadius: '50%', display: 'inline-block' }}></span>
                    <span>Finalizada</span>
                  </span>
                </div>
              </div>
            </>
          )}
          
          {tipoMapa === 'postes' && (
            <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
              <p style={{ margin: '0' }}>
                Total: {postesFiltradasPorStatus.length} postes exibidos
              </p>
            </div>
          )}
          
          {tipoMapa === 'fossas' && (
            <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
              <p style={{ margin: '0' }}>
                {mostrarPropriedades ? propriedadesFiltradasPorStatus.length : 0} propriedades, {' '}
                {mostrarResidencias ? residenciasFiltradasPorStatus.length : 0} residências, {' '}
                {mostrarFossas ? fossasFiltradasPorStatus.length : 0} fossas
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Modal de detalhes da obra linear */}
      {obraSelecionada && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Detalhes da Obra Linear</h3>
              <button
                onClick={() => setObraSelecionada(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>
            
            <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraSelecionada.nome}</h4>
            
            <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
              <div>
                <strong>Empreiteiro:</strong> {obraSelecionada.empreiteiro}
              </div>
              <div>
                <strong>Valor:</strong> {formatarMoeda(obraSelecionada.valor)}
              </div>
              <div>
                <strong>Status:</strong> 
                <span 
                  style={{ 
                    color: getCorPorStatus(obraSelecionada.status),
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}
                >
                  {obraSelecionada.status}
                </span>
              </div>
              <div>
                <strong>Data de Início:</strong> {formatarData(obraSelecionada.dataInicio)}
              </div>
              <div>
                <strong>Previsão de Término:</strong> {formatarData(obraSelecionada.dataPrevisaoTermino)}
              </div>
            </div>
            
            {obraSelecionada.itens && obraSelecionada.itens.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Progresso:</strong> {formatarPercentual(calcularProgressoGeral(obraSelecionada.itens))}
                <div style={{ 
                  width: '100%', 
                  height: '10px', 
                  backgroundColor: '#e9ecef',
                  borderRadius: '5px',
                  overflow: 'hidden',
                  marginTop: '5px'
                }}>
                  <div style={{ 
                    width: `${calcularProgressoGeral(obraSelecionada.itens)}%`, 
                    height: '100%', 
                    backgroundColor: getCorPorStatus(obraSelecionada.status),
                    borderRadius: '5px'
                  }}></div>
                </div>
              </div>
            )}
            
            {obraSelecionada.observacoes && (
              <div>
                <strong>Observações:</strong>
                <p style={{ 
                  marginTop: '8px', 
                  padding: '10px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '6px',
                  fontSize: '14px'
                }}>
                  {obraSelecionada.observacoes}
                </p>
              </div>
            )}
            
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Link 
                to={`/obras/medicoes/${obraSelecionada.id}`}
                style={{
                  backgroundColor: '#0d6efd',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <Eye size={16} />
                Ver Detalhes Completos
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de detalhes da obra fixa */}
      {obraFixaSelecionada && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Detalhes da Obra Fixa</h3>
              <button
                onClick={() => setObraFixaSelecionada(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                <X size={20} color="#666" />
              </button>
            </div>
            
            <h4 style={{ color: '#333', marginBottom: '15px' }}>{obraFixaSelecionada.nome}</h4>
            
            <div style={{ display: 'grid', gap: '10px', marginBottom: '15px' }}>
              <div>
                <strong>Empreiteiro:</strong> {obraFixaSelecionada.empreiteiro}
              </div>
              <div>
                <strong>Valor:</strong> {formatarMoeda(obraFixaSelecionada.valor)}
              </div>
              <div>
                <strong>Status:</strong> 
                <span 
                  style={{ 
                    color: getCorPorStatus(obraFixaSelecionada.status),
                    fontWeight: 'bold',
                    marginLeft: '8px'
                  }}
                >
                  {obraFixaSelecionada.status}
                </span>
              </div>
              <div>
                <strong>Data de Início:</strong> {formatarData(obraFixaSelecionada.dataInicio)}
              </div>
              <div>
                <strong>Previsão de Término:</strong> {formatarData(obraFixaSelecionada.dataPrevisaoTermino)}
              </div>
              {obraFixaSelecionada.localizacao && obraFixaSelecionada.localizacao.nome && (
                <div>
                  <strong>Local:</strong> {obraFixaSelecionada.localizacao.nome}
                </div>
              )}
              {obraFixaSelecionada.localizacao && obraFixaSelecionada.localizacao.descricao && (
                <div>
                  <strong>Descrição do Local:</strong> {obraFixaSelecionada.localizacao.descricao}
                </div>
              )}
            </div>
            
            {obraFixaSelecionada.itens && obraFixaSelecionada.itens.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Progresso:</strong> {formatarPercentual(calcularProgressoGeral(obraFixaSelecionada.itens))}
                <div style={{ 
                  width: '100%', 
                  height: '10px', 
                  backgroundColor: '#e9ecef',
                  borderRadius: '5px',
                  overflow: 'hidden',
                  marginTop: '5px'
                }}>
                  <div style={{ 
                    width: `${calcularProgressoGeral(obraFixaSelecionada.itens)}%`, 
                    height: '100%', 
                    backgroundColor: getCorPorStatus(obraFixaSelecionada.status),
                    borderRadius: '5px'
                  }}></div>
                </div>
              </div>
            )}
            
            {obraFixaSelecionada.observacoes && (
              <div>
                <strong>Observações:</strong>
                <p style={{ 
                  marginTop: '8px', 
                  padding: '10px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '6px',
                  fontSize: '14px'
                }}>
                  {obraFixaSelecionada.observacoes}
                </p>
              </div>
            )}
            
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Link 
                to={`/obras-fixas/medicoes/${obraFixaSelecionada.id}`}
                style={{
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <Eye size={16} />
                Ver Detalhes Completos
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente wrapper com todos os providers necessários
const MapaIntegrado = () => {
  return (
    <PostesIluminacaoProvider>
      <FossasSepticasProvider>
        <MapaIntegradoContent />
      </FossasSepticasProvider>
    </PostesIluminacaoProvider>
  );
};

export default MapaIntegrado;